import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import API_URL from '../../config/api';
import { getAudience } from '../../config/permissions';
import { SYSTEM_PAGE_CSS } from '../landlord/systemPageStyles';
import { apiFetch } from '../../utils/apiFetch';

interface Customer {
  id: string | number;
  name: string;
  email?: string;
}

interface Recipient {
  key: string;       // unique id for list
  name: string;
  email: string;
  isGuest: boolean;
}

const TEMPLATES = [
  { label: 'New Update',    subject: 'Exciting Update from Us!',              body: 'Hi {name},\n\nWe wanted to share some exciting news with you...\n\nBest regards' },
  { label: 'Promotion',     subject: 'Exclusive Offer Just for You 🎉',        body: 'Hi {name},\n\nAs a valued customer, we have a special offer for you...\n\nBest regards' },
  { label: 'Invoice Reminder', subject: 'Friendly Payment Reminder',          body: 'Hi {name},\n\nThis is a friendly reminder about your upcoming invoice...\n\nThank you' },
  { label: 'Thank You',     subject: 'Thank You for Your Business!',           body: 'Hi {name},\n\nWe truly appreciate your continued support...\n\nWarm regards' },
  { label: 'Appointment',   subject: 'Appointment Confirmation',               body: 'Hi {name},\n\nYour appointment has been confirmed. Please let us know if you need to reschedule...\n\nBest regards' },
];

const LANDLORD_TEMPLATES = [
  { label: 'Rent reminder', subject: 'Rent payment reminder', body: 'Hi {name},\n\nThis is a friendly reminder that your rent payment is due shortly. Please let us know if you have any questions.\n\nKind regards' },
  { label: 'Maintenance', subject: 'Maintenance update', body: 'Hi {name},\n\nWe wanted to update you on a maintenance matter at your property. Please get in touch if you need anything further.\n\nKind regards' },
  { label: 'Viewing', subject: 'Viewing confirmation', body: 'Hi {name},\n\nYour property viewing has been confirmed. Please arrive on time and bring photo ID.\n\nKind regards' },
  { label: 'General', subject: 'Message from your landlord', body: 'Hi {name},\n\nWe wanted to get in touch regarding your tenancy.\n\nKind regards' },
];

const SendMessage: React.FC = () => {
  const isLandlord = getAudience(localStorage.getItem('business_type')) === 'landlord';
  const [customers,     setCustomers]     = useState<Customer[]>([]);
  const [recipients,    setRecipients]    = useState<Recipient[]>([]);
  const [searchText,    setSearchText]    = useState('');
  const [suggestions,   setSuggestions]   = useState<Customer[]>([]);
  const [showDropdown,  setShowDropdown]  = useState(false);
  const [guestEmail,    setGuestEmail]    = useState('');
  const [msgUsage,      setMsgUsage]      = useState<{used:number,limit:number}|null>(null);
  const [guestName,     setGuestName]     = useState('');
  const [subject,       setSubject]       = useState('');
  const [body,          setBody]          = useState('');
  const [sending,       setSending]       = useState(false);
  const [history,       setHistory]       = useState<any[]>([]);
  const [tab,           setTab]           = useState<'compose'|'history'>('compose');
  const [historyLoading, setHistoryLoading] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // Fetch recipients on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (isLandlord) {
      apiFetch(`${API_URL}/landlord/tenants`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : { tenants: [] })
        .then(d => setCustomers((d.tenants || []).map((t: any) => ({
          id: t.id,
          name: t.full_name || t.name || 'Tenant',
          email: t.email,
        }))))
        .catch(() => toast.error('Failed to load tenants'));
    } else {
      axios.get(`${API_URL}/customers`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setCustomers(r.data || []))
        .catch(() => toast.error('Failed to load customers'));
    }

    axios.get(`${API_URL}/message/usage`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMsgUsage(r.data))
      .catch(() => {
        console.warn('Failed to load message usage');
      });
  }, [isLandlord]);

  // Fetch message history when tab switches
  useEffect(() => {
    if (tab === 'history') fetchHistory();
  }, [tab]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/message?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(r.data.data || []);
    } catch { toast.error('Failed to load history'); }
    finally { setHistoryLoading(false); }
  };

  // Autocomplete: filter customers by typed name
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchText(val);
    if (val.trim().length === 0) { setSuggestions([]); setShowDropdown(false); return; }
    const filtered = customers
      .filter(c => c.name.toLowerCase().includes(val.toLowerCase()) && c.email)
      .filter(c => !recipients.some(r => String(r.email) === String(c.email)))
      .slice(0, 8);
    setSuggestions(filtered);
    setShowDropdown(true);
  };

  const addCustomer = (c: Customer) => {
    if (!c.email) { toast.warning(`${c.name} has no email address`); return; }
    if (recipients.some(r => r.email === c.email)) { toast.info('Already added'); return; }
    setRecipients(prev => [...prev, { key: String(c.id), name: c.name, email: c.email!, isGuest: isLandlord }]);
    setSearchText('');
    setSuggestions([]);
    setShowDropdown(false);
  };

  const addGuest = () => {
    const email = guestEmail.trim();
    const name  = guestName.trim() || email;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Enter a valid email address'); return; }
    if (recipients.some(r => r.email === email)) { toast.info('Already added'); return; }
    setRecipients(prev => [...prev, { key: `guest-${Date.now()}`, name, email, isGuest: true }]);
    setGuestEmail('');
    setGuestName('');
  };

  const removeRecipient = (key: string) => setRecipients(prev => prev.filter(r => r.key !== key));

  const applyTemplate = (tpl: typeof TEMPLATES[0]) => {
    setSubject(tpl.subject);
    setBody(tpl.body);
  };

  const addAllCustomers = () => {
    const toAdd = customers.filter(c => c.email && !recipients.some(r => r.email === c.email));
    if (toAdd.length === 0) { toast.info(isLandlord ? 'All tenants already added' : 'All customers already added'); return; }
    setRecipients(prev => [
      ...prev,
      ...toAdd.map(c => ({ key: String(c.id), name: c.name, email: c.email!, isGuest: isLandlord }))
    ]);
    toast.success(`Added ${toAdd.length} ${isLandlord ? 'tenant' : 'customer'}(s)`);
  };

  const activeTemplates = isLandlord ? LANDLORD_TEMPLATES : TEMPLATES;
  const recipientLabel = isLandlord ? 'tenant' : 'customer';

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (recipients.length === 0) { toast.error('Add at least one recipient'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim())    { toast.error('Message body is required'); return; }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const recipientPayload = recipients.map(r => (r.isGuest || isLandlord)
        ? { email: r.email, name: r.name }
        : { customerId: r.key, email: r.email, name: r.name }
      );
      const res = await axios.post(
        `${API_URL}/message/send-bulk`,
        { recipients: recipientPayload, subject: subject.trim(), body: body.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { sent, errors } = res.data;
      if (sent.length > 0) toast.success(`✅ Sent to ${sent.length} recipient(s)`);
      if (errors.length > 0) toast.warning(`⚠️ ${errors.length} failed to send`);
      setRecipients([]);
      setSubject('');
      setBody('');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const statusColor = (s: string) => ({ sent: '#16a34a', queued: '#d97706', failed: '#dc2626' }[s] || '#888');

  const exportHistoryCSV = () => {
    if (history.length === 0) return;
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Date', 'Subject', 'Recipient', 'Status'];
    const rows = history.map((m: any) => [
      esc(new Date(m.created_at).toLocaleString('en-GB')),
      esc(m.subject || ''),
      esc(m.customer_name || m.metadata?.recipientEmail || ''),
      esc(m.status || ''),
    ]);
    const csv = [header.join(','), ...rows.map(r => r.join(','))].join('\n');
    const uri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    const a = document.createElement('a');
    a.href = uri;
    a.download = `messages_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className={isLandlord ? 'sys-page' : 'page-layout'}>
      {isLandlord && <style>{SYSTEM_PAGE_CSS}</style>}
      {isLandlord ? (
        <div className="sys-banner-wrap sys-banner-sales">
          <div className="sys-banner-glass">
            <div>
              <h1>📨 Messages</h1>
              <p>Email tenants directly — one tenant, a property group, or everyone on your books</p>
            </div>
            {msgUsage && (
              <div className="sys-banner-actions">
                <span className="sys-btn-glass">{msgUsage.used} / {msgUsage.limit} this month</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="page-header">
          <h1>📨 Send Message</h1>
          <p>Send emails to customers or any email address</p>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 24, maxWidth: 1100, margin: isLandlord ? '0 0 24px' : '0 auto 24px', padding: isLandlord ? 0 : '0 12px' }}>
        {(['compose', 'history'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '10px 28px', border: 'none', borderRadius: 7, cursor: 'pointer',
            background: tab === t ? '#2563eb' : '#f3f4f6',
            color: tab === t ? '#fff' : '#374151',
            fontWeight: tab === t ? 700 : 400, fontSize: 15
          }}>
            {t === 'compose' ? '✉️ Compose' : '📋 History'}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 clamp(8px, 3vw, 20px)' }}>

        {/* ── COMPOSE TAB ── */}
        {tab === 'compose' && (
          <form onSubmit={handleSend} style={{ background: '#fff', borderRadius: 12, padding: 'clamp(16px, 4vw, 32px)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>

            {/* Templates */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14, color: '#374151' }}>⚡ Quick Templates</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {activeTemplates.map((tpl, i) => (
                  <button key={i} type="button" onClick={() => applyTemplate(tpl)} style={{
                    padding: '5px 12px', border: '1px solid #d1d5db', borderRadius: 20,
                    background: '#f9fafb', color: '#374151', cursor: 'pointer', fontSize: 13
                  }}>
                    {tpl.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Recipients */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontWeight: 600, fontSize: 14 }}>To: (Recipients) *</label>
                <button type="button" onClick={addAllCustomers} style={{
                  padding: '3px 12px', border: '1px solid #2563eb', borderRadius: 6,
                  background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontSize: 12, fontWeight: 600
                }}>
                  + All {isLandlord ? 'Tenants' : 'Customers'}
                </button>
              </div>

              {/* Selected recipient badges */}
              {recipients.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {recipients.map(r => (
                    <span key={r.key} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '4px 10px', borderRadius: 20,
                      background: r.isGuest ? '#fef3c7' : '#eff6ff',
                      border: `1px solid ${r.isGuest ? '#f59e0b' : '#93c5fd'}`,
                      fontSize: 13, color: '#1e3a8a'
                    }}>
                      {r.isGuest ? '👤' : '🙍'} {r.name}
                      <span style={{ fontSize: 11, color: '#6b7280' }}>&lt;{r.email}&gt;</span>
                      <button type="button" onClick={() => removeRecipient(r.key)} style={{
                        background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af',
                        fontSize: 14, lineHeight: 1, padding: 0
                      }}>×</button>
                    </span>
                  ))}
                </div>
              )}

              {/* Customer search autocomplete */}
              <div style={{ position: 'relative', marginBottom: 10 }} ref={searchRef as any}>
                <input
                  value={searchText}
                  onChange={handleSearchChange}
                  onFocus={() => searchText && setShowDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                  placeholder={`🔍 Type ${recipientLabel} name to search…`}
                  style={{
                    width: '100%', padding: '9px 12px', borderRadius: 6,
                    border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box'
                  }}
                />
                {showDropdown && suggestions.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                    background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 240, overflowY: 'auto'
                  }}>
                    {suggestions.map(c => (
                      <div key={c.id} onMouseDown={() => addCustomer(c)} style={{
                        padding: '10px 14px', cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                      }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                        onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                      >
                        <span style={{ fontWeight: 600 }}>{c.name}</span>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>{c.email}</span>
                      </div>
                    ))}
                  </div>
                )}
                {showDropdown && suggestions.length === 0 && searchText.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                    background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
                    padding: '12px 14px', color: '#9ca3af', fontSize: 13
                  }}>
                    No {recipientLabel}s found for "{searchText}"
                  </div>
                )}
              </div>

              {/* Guest email */}
              <div style={{ background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>👤 Add email manually (not on your {recipientLabel} list)</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    value={guestName}
                    onChange={e => setGuestName(e.target.value)}
                    placeholder="Name (optional)"
                    style={{ flex: '1 1 140px', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
                  />
                  <input
                    value={guestEmail}
                    onChange={e => setGuestEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addGuest())}
                    placeholder="Email address"
                    type="email"
                    style={{ flex: '2 1 200px', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
                  />
                  <button type="button" onClick={addGuest} style={{
                    padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db',
                    borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13
                  }}>Add</button>
                </div>
              </div>
            </div>

            {/* Subject */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 6 }}>Subject *</label>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Enter email subject..."
                required
                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>

            {/* Body */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                Message *
                <span style={{ float: 'right', fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>
                  Use {'{name}'} to personalise
                </span>
              </label>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Type your message here... Use {name} to personalise per recipient"
                rows={10}
                required
                style={{ width: '100%', padding: '12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 15, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', minHeight: 200 }}
              />
            </div>

            {/* Preview */}
            {recipients.length > 0 && body && (
              <div style={{ marginBottom: 20, padding: 16, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10, fontWeight: 600 }}>Preview (as seen by first recipient)</div>
                <div style={{ background: '#fff', padding: 14, borderRadius: 6, fontSize: 14 }}>
                  {subject && <div style={{ fontWeight: 700, marginBottom: 8 }}>Subject: {subject}</div>}
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {body.replace(/\{name\}/g, recipients[0]?.name || 'Customer')}
                  </div>
                </div>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={sending || recipients.length === 0 || !body || !subject}
              style={{
                width: '100%', padding: 14,
                background: sending || recipients.length === 0 || !body || !subject ? '#9ca3af' : '#2563eb',
                color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 16, fontWeight: 700, cursor: sending || recipients.length === 0 || !body || !subject ? 'not-allowed' : 'pointer'
              }}
            >
              {sending ? '⏳ Sending…' : `📤 Send to ${recipients.length} Recipient${recipients.length !== 1 ? 's' : ''}`}
            </button>
            {msgUsage && (
              <div style={{marginTop:12,padding:'10px 14px',background:'#f9fafb',
                borderRadius:8,border:'1px solid #e5e7eb'}}>
                <div style={{display:'flex',justifyContent:'space-between',
                  fontSize:13,marginBottom:6}}>
                  <span style={{fontWeight:600}}>📨 Monthly Messages</span>
                  <span style={{color: msgUsage.used >= 300 ? '#ef4444' : '#6b7280'}}>
                    {msgUsage.used} / {msgUsage.limit}
                  </span>
                </div>
                <div style={{background:'#e5e7eb',borderRadius:4,height:6}}>
                  <div style={{
                    background: msgUsage.used >= 300 ? '#ef4444' : '#10b981',
                    width: `${Math.min((msgUsage.used/msgUsage.limit)*100,100)}%`,
                    height:'100%',borderRadius:4,transition:'width 0.3s'
                  }}/>
                </div>
                {msgUsage.used >= 300 && (
                  <p style={{margin:'6px 0 0',fontSize:12,color:'#ef4444',fontWeight:600}}>
                    ⚠️ Approaching limit — 369 messages/month free during beta
                  </p>
                )}
              </div>
            )}
          </form>
        )}

        {/* ── HISTORY TAB ── */}
        {tab === 'history' && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 'clamp(16px, 4vw, 24px)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Message History</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={fetchHistory} style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#f9fafb', cursor: 'pointer', fontSize: 13 }}>
                  🔄 Refresh
                </button>
                <button onClick={exportHistoryCSV} disabled={history.length === 0} style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#f9fafb', cursor: history.length === 0 ? 'not-allowed' : 'pointer', fontSize: 13, opacity: history.length === 0 ? 0.5 : 1 }}>
                  ⬇️ Export CSV
                </button>
              </div>
            </div>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</div>
            ) : history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No messages sent yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {history.map((m: any) => (
                  <div key={m.id} style={{ padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{m.subject || '(no subject)'}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        To: {m.customer_name || m.metadata?.recipientEmail || '—'} &nbsp;•&nbsp; {new Date(m.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700, color: '#fff', background: statusColor(m.status) }}>
                      {m.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SendMessage;
